import { Load, Section } from '../ui/kit';

// The admin journey card (modelled on Lendean's admin user page): funnel chips, a summary line, and a scroll box of
// visits newest first, each with a sticky header and its events oldest first. Data: GET /ops/people/:id/journey or
// /ops/orgs/:id/journey (src/growth/journey.ts). Product milestones come from the product tables, so a person who
// signed up before tracking began still has a timeline.

export type JourneyEvent = { at: string; type: string; name: string | null; page: string | null };
export type Visit = { session_id: string; kind: 'visit' | 'activity'; started_at: string; last_at: string; pages: number; referrer: string | null; events: JourneyEvent[] };
export type JourneyData = { funnel: Array<{ label: string; reached: boolean; note: string | null }>; visits: Visit[]; totals?: { events: number; shown: number } };

const svg = (d: string) => <svg className="jr-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false"><path d={d} /></svg>;

/** type → [icon path, label, milestone]: every type the trackers and the milestones produce has its own entry */
export const EVENT_TYPES: Record<string, [string, string, boolean]> = {
  page_view: ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6', 'page', false],
  cta_click: ['M9 9l5 12 1.8-5.2L21 14zM7.2 2.2 8 5.1M2.2 7.2l2.9.8M14 4.1 12 6.2M5.1 8 3 10.1', 'pressed', false],
  form_start: ['M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z', 'started a form', false],
  form_submit: ['M22 2 11 13M22 2l-7 20-4-9-9-4z', 'form sent', false],
  section_view: ['M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8zM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z', 'saw section', false],
  scroll_milestone: ['M12 5v14M19 12l-7 7-7-7', 'scrolled', false],
  experiment: ['M9 3h6M10 3v6L4 19a2 2 0 0 0 2 3h12a2 2 0 0 0 2-3L14 9V3', 'site event', false],
  signed_up: ['M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM19 8v6M22 11h-6', 'Signed up', true],
  workspace_created: ['M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6', 'Workspace created', true],
  assessment: ['M9 2h6v4H9zM16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2M9 14l2 2 4-4', 'Free assessment', true],
  website_read: ['M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM2 12h20M12 2a15 15 0 0 1 0 20M12 2a15 15 0 0 0 0 20', 'Website read', true],
  icp_approved: ['M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM12 18a6 6 0 1 0 0-12 6 6 0 0 0 0 12zM12 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4z', 'ICP approved', true],
  accounts_added: ['M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01', 'Accounts added', true],
  sender_address: ['M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0zM12 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6z', 'Sender address', true],
  sending_set_up: ['M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2zM22 6l-10 7L2 6', 'Sending set up', true],
  campaign_created: ['M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2zM12 8v8M8 12h8', 'Campaign created', true],
  campaign_live: ['M5 19c4-1 6-3 7-7l3-3a3 3 0 0 0-4-4l-3 3c-4 1-6 3-7 7zM14 6l4 4M5 19l2-5 3 3z', 'Campaign live', true],
  first_reply: ['M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z', 'First reply', true],
  meeting_booked: ['M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2zM9 16l2 2 4-4', 'Meeting booked', true],
  paid: ['M3 5h18a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2zM1 10h22', 'Paid', true],
  assistant: ['M12 3l1.9 5.8L20 10l-6.1 1.2L12 17l-1.9-5.8L4 10l6.1-1.2z', 'Assistant', true],
};
const FALLBACK: [string, string, boolean] = ['M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z', 'event', false];
const CHECK = 'M20 6 9 17l-5-5';
const DASH = 'M5 12h14';

const pad = (n: number) => String(n).padStart(2, '0');
const day = (d: Date) => `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
const hm = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
const hms = (d: Date) => `${hm(d)}:${pad(d.getSeconds())}`;
const cut = (s: string | null | undefined, n: number) => (!s ? '' : s.length > n ? `${s.slice(0, n - 1)}…` : s);

export function JourneyView({ data }: { data: JourneyData }) {
  const events = data.visits.reduce((n, v) => n + v.events.length, 0);
  const visits = data.visits.filter((v) => v.kind === 'visit').length;
  return <>
    {data.funnel.length ? <ul className="jr-funnel" aria-label="Funnel">
      {data.funnel.map((c) => (
        <li key={c.label} className={`jr-chip${c.reached ? ' jr-chip-on' : ''}`}>
          {svg(c.reached ? CHECK : DASH)}<span>{c.label}</span>{c.reached && c.note ? <span className="jr-note"> · {c.note}</span> : null}
          <span className="sr-only">{c.reached ? ' (reached)' : ' (not reached)'}</span>
        </li>))}
    </ul> : null}
    {!data.visits.length ? <p className="muted">No events recorded.
      <span className="small" style={{ display: 'block' }}>The app and outlevo.com record visits from 22 October 2026, and only outlevo.com visits from a browser without Global Privacy Control or Do Not Track are linked at sign-up; product milestones appear here as soon as they happen.</span></p> : <>
      <p className="muted small jr-summary">visits: {visits} · events: {events}{data.totals && data.totals.events > data.totals.shown ? ` (the latest ${data.totals.shown} of ${data.totals.events} tracked)` : ''} — the list scrolls</p>
      <div className="jr-scroll" tabIndex={0} role="region" aria-label="Visits, newest first">
        {data.visits.map((v) => {
          const from = new Date(v.started_at), to = new Date(v.last_at);
          return <div key={v.session_id} className="jr-visit">
            <div className="jr-head">
              <b>{day(from)} {hm(from)}</b>
              <span className="muted small">— {hm(to)} · {v.kind === 'visit' ? `pages ${v.pages} · ` : 'product activity · '}events {v.events.length}</span>
              {v.referrer ? <span className="muted small">← {cut(v.referrer.replace(/^https?:\/\//, ''), 46)}</span> : null}
            </div>
            <ol className="jr-rows">
              {v.events.map((e, i) => {
                const [icon, label, milestone] = EVENT_TYPES[e.type] ?? FALLBACK;
                return <li key={i} className={milestone ? 'jr-milestone' : undefined}>
                  <time className="jr-time muted" dateTime={e.at}>{hms(new Date(e.at))}</time>
                  <span className="jr-ico">{svg(icon)}</span>
                  <span className="jr-what">
                    {milestone ? <><b>{label}</b>{e.name ? <span className="muted"> — {cut(e.name, 70)}</span> : null}</>
                      : e.type === 'cta_click' || e.type === 'form_submit' ? <><b>{cut(e.name, 70) || label}</b> <span className="muted">— {label}</span></>
                      : <><span className="muted">{label}</span>{e.name && e.type !== 'page_view' ? <span> {cut(e.name, 50)}</span> : null}</>}
                    {e.page ? <span className="jr-page">{cut(e.page, 78)}</span> : null}
                  </span>
                </li>;
              })}
            </ol>
          </div>;
        })}
      </div>
    </>}
  </>;
}

/** The card: "Path through the site" for a person or a workspace. */
export function JourneyCard({ path, title = 'Path through the site' }: { path: string; title?: string }) {
  return <Section title={title}><Load<JourneyData> path={path}>{(d) => <JourneyView data={d} />}</Load></Section>;
}
